import { Button } from '@components/Button'
import { useNetwork } from '@hooks/useNetwork'
import { useForumPostCreate } from '@services/api/graphql/mutations/forumPostCreate'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TextArea, XStack, YStack } from 'tamagui'

type ToastType = 'success' | 'error'

interface IPostFormProps {
  topicId: string
  onCreated?: () => void
  onToast?: (type: ToastType, message: string) => void
}

export function PostForm({ topicId, onCreated, onToast }: IPostFormProps) {
  const { t } = useTranslation('Comments')
  const { isConnected } = useNetwork()
  const { handleCreatePost, loading } = useForumPostCreate()
  const [body, setBody] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Guarda de reentrância síncrona: o `loading` do useMutation só vira true no
  // próximo render, então um mesmo clique que dispare onPress duas vezes no
  // mesmo tick criaria duas respostas (duplicação). O ref bloqueia a 2ª chamada
  // imediatamente, antes de qualquer await.
  const isSubmittingRef = useRef(false)

  const onSubmit = async () => {
    if (isSubmittingRef.current) return

    if (!body.trim()) {
      setError(t('newPost.validations.bodyRequired'))
      return
    }

    if (!isConnected) {
      onToast?.('error', t('newPost.offline'))
      return
    }

    isSubmittingRef.current = true
    try {
      const result = await handleCreatePost({ topicId, body: body.trim() })
      if (result?.errors && result.errors.length > 0) {
        onToast?.('error', result.errors[0])
        return
      }
      setBody('')
      setError(null)
      onCreated?.()
      onToast?.('success', t('newPost.success'))
    } catch {
      onToast?.('error', t('newPost.error'))
    } finally {
      isSubmittingRef.current = false
    }
  }

  return (
    <YStack gap="$spacing-inset-xs" testID="forum-post-form">
      <TextArea
        value={body}
        onChangeText={(value) => {
          setBody(value)
          if (error) setError(null)
        }}
        placeholder={t('newPost.placeholder')}
        borderColor={error ? '$danger-300' : '$border-default'}
        backgroundColor="$bg-input"
        fontFamily="$mulishRegular"
        fontSize="$font-size-xxs"
        color="$text-primary"
        placeholderTextColor="$text-placeholder"
        minHeight={80}
        testID="forum-post-form-body"
      />
      <XStack justifyContent="flex-end">
        <Button
          variant="primary"
          size="small"
          onPress={onSubmit}
          disabled={loading}
          loading={loading}
          testID="forum-post-form-submit"
        >
          {t('newPost.submit')}
        </Button>
      </XStack>
    </YStack>
  )
}
